/**
 * État d'une lecture de Mon compte v2 (GET client/visits, client/zingfit-visits) d'après le résultat de useQuery
 * (react-query 5.8) : reçue, en cours ou en échec. Partagé par Mes chiffres, Mes cours à venir et Mon historique
 * (décision de DA du 09/10 : une lecture en échec n'est jamais montrée comme un résultat vide).
 *
 * Hors ligne (relecture du 09/10) : react-query ne fait pas échouer une lecture sans données, il la MET EN PAUSE
 * (fetchStatus 'paused', status 'pending' ; isLoading = isPending && isFetching est alors faux, isError aussi). Un
 * calcul sur isLoading et isError seuls la prenait pour une lecture terminée et vide (« 0 · Pas encore de cours »,
 * « Rien de réservé pour l'instant »). Une lecture en pause sans données compte donc comme un échec ; au retour en
 * ligne, react-query la reprend seul.
 */
import type { QueryClient, QueryKey, QueryState } from '@tanstack/react-query';

/** Sous-ensemble du résultat de useQuery. */
export interface QuerySource {
  data?: unknown;
  /** status 'pending' : rien reçu (en cours, en pause, ou pas encore lancée). */
  isPending?: boolean;
  /** fetchStatus 'paused' : lecture en pause (navigateur hors ligne). */
  isPaused?: boolean;
  isError?: boolean;
}

/** En échec : erreur ou pause, rien en cache (une nouvelle lecture en erreur garde ses données : ce n'est pas un échec). */
export function sourceFailed(source: QuerySource): boolean {
  return (!!source.isError || !!source.isPaused) && source.data === undefined;
}

/** En cours : rien reçu, ni en erreur ni en pause. */
export function sourcePending(source: QuerySource): boolean {
  return !!source.isPending && !sourceFailed(source);
}

/** Même règle sur l'état interne d'une requête du cache (QueryCache). */
function stateFailed(state: QueryState): boolean {
  return state.data === undefined && (state.status === 'error' || state.fetchStatus === 'paused');
}

/**
 * Relit une source en échec SANS repasser sa requête en attente (« Réessayer » de Mes chiffres) : Mes cours à venir et
 * Mon historique lisent les mêmes visites, un refetch les ramenait à leur squelette pendant la lecture (Mes chiffres
 * descendait de 47 à 82 px, le bouton focalisé sortait de la fenêtre). La requête observée en échec dont la clé commence
 * par `prefix` (clé [chemin, client] : le client suit) reçoit la réponse de `read` dans le cache : toutes les rubriques
 * changent à la fois ; nouvel échec : rien ne bouge. Requête introuvable (clé changée) : `refetch`, en repli. Vrai si la
 * source est lue.
 */
export async function rereadFailedSource(
  queryClient: QueryClient,
  prefix: QueryKey,
  read: () => Promise<unknown>,
  refetch?: () => Promise<{ isError: boolean }>,
): Promise<boolean> {
  const query = queryClient.getQueryCache().find({
    queryKey: prefix,
    // find() compare la clé entière par défaut : ici, le préfixe.
    exact: false,
    type: 'active',
    predicate: (candidate) => stateFailed(candidate.state),
  });
  try {
    if (!query) return refetch ? !(await refetch()).isError : false;
    queryClient.setQueryData(query.queryKey, await read());
    return true;
  } catch {
    return false;
  }
}
